import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { privacyAPI } from '../lib/api';

const SLA_DAYS = 30; // GDPR response window
const TYPE_LABELS = {
  access: 'Access', delete: 'Delete', correct: 'Correct',
  portability: 'Portability', object: 'Object', withdraw_consent: 'Withdraw consent',
};
const STATUSES = ['received', 'in_progress', 'resolved', 'rejected'];
const STATUS_STYLE = {
  received: { bg: '#FFFBEB', color: '#B45309' },
  in_progress: { bg: '#FFE7D9', color: '#0076A3' },
  resolved: { bg: '#ECFDF5', color: '#059669' },
  rejected: { bg: '#FEF2F2', color: '#DC2626' },
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const slaInfo = (createdAt, status) => {
  if (status === 'resolved' || status === 'rejected') return { label: 'Closed', color: '#94A3B8' };
  if (!createdAt) return { label: '—', color: '#94A3B8' };
  const due = new Date(createdAt).getTime() + SLA_DAYS * 86400000;
  const days = Math.ceil((due - Date.now()) / 86400000);
  if (days < 0) return { label: `Overdue ${Math.abs(days)}d`, color: '#DC2626' };
  if (days <= 5) return { label: `${days}d left`, color: '#B45309' };
  return { label: `${days}d left`, color: '#059669' };
};

export default function DataRequestsAdminPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await privacyAPI.getDataRequests();
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error('Failed to load data requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return requests.filter((r) => {
      const matchesTerm = !term
        || (r.name || '').toLowerCase().includes(term)
        || (r.email || '').toLowerCase().includes(term);
      const matchesStatus = !statusFilter || r.status === statusFilter;
      return matchesTerm && matchesStatus;
    });
  }, [requests, q, statusFilter]);

  const counts = useMemo(() => {
    const c = { open: 0, overdue: 0 };
    requests.forEach((r) => {
      if (r.status !== 'resolved' && r.status !== 'rejected') {
        c.open += 1;
        if (slaInfo(r.created_at, r.status).label.startsWith('Overdue')) c.overdue += 1;
      }
    });
    return c;
  }, [requests]);

  const updateStatus = async (id, status) => {
    setSavingId(id);
    try {
      await privacyAPI.updateDataRequest(id, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status, resolved_at: (status === 'resolved' || status === 'rejected') ? new Date().toISOString() : null } : r)));
      toast.success('Request updated');
    } catch (e) {
      toast.error('Could not update request');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1.5rem' }} data-testid="data-requests-admin-page">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 700, color: '#0076A3', margin: 0 }}>Data Requests</h1>
        <p style={{ fontSize: '0.9375rem', color: '#718096', marginTop: '0.375rem' }}>
          Track and resolve data-subject requests. <strong>{counts.open}</strong> open
          {counts.overdue > 0 && <span style={{ color: '#DC2626', fontWeight: 600 }}> · {counts.overdue} overdue</span>}.
        </p>
      </div>

      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <input
            data-testid="dr-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            style={{ flex: '1 1 260px', padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }}
          />
          <select data-testid="dr-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <Button data-testid="dr-refresh-btn" onClick={load} variant="outline" style={{ borderRadius: 10 }}>Refresh</Button>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#A0AEC0' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: '#718096' }} data-testid="dr-empty">No data requests found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#A0AEC0', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Received</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Requester</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Type</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>SLA</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Status</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const sla = slaInfo(r.created_at, r.status);
                  const st = STATUS_STYLE[r.status] || STATUS_STYLE.received;
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid #F0EBE3' }} data-testid="dr-row">
                      <td style={{ padding: '0.75rem 0.5rem', color: '#4A5568', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <div style={{ fontWeight: 600, color: '#0076A3' }}>{r.name || '—'}</div>
                        <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{r.email}</div>
                        {r.region && <div style={{ fontSize: '0.625rem', color: '#CBD5E1' }}>{r.region}</div>}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', color: '#0076A3', fontWeight: 600 }}>{TYPE_LABELS[r.request_type] || r.request_type}</td>
                      <td style={{ padding: '0.75rem 0.5rem', fontWeight: 700, color: sla.color }}>{sla.label}</td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: 999, background: st.bg, color: st.color, textTransform: 'capitalize' }}>{(r.status || '').replace('_', ' ')}</span>
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <select
                          data-testid={`dr-status-select-${r.id}`}
                          value={r.status}
                          disabled={savingId === r.id}
                          onChange={(e) => updateStatus(r.id, e.target.value)}
                          style={{ padding: '0.35rem 0.5rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
